<template>
  <BasePaymentRequestsTable
    v-model:selection-mode="selectionMode"
    :query="query"
    data-test="payment-requests-payrolled-table"
    :is-active-tab="isActiveTab"
    :bulk-actions="bulkActions"
    :batch-ids="batchSelection.selectedBatchIds.value"
    :enabled-row-selection-modes="enabledRowSelectionModes"
  >
    <template #action="{ row }">
      <PaymentRequestActionColumnPayrolled :row="row" />
    </template>
    <template #filters>
      <BatchFilterMultiSelect
        data-test="payment-requests-payrolled-batch-filter"
        :batches="batchSelection.batches.value"
        :selected-batch-ids="batchSelection.selectedBatchIds.value"
        @update:selected-batch-ids="handleBatchSelectionUpdated"
      />
    </template>
  </BasePaymentRequestsTable>
</template>

<script lang="ts" setup>
import { TypedQueryDocumentNode } from 'graphql/index'
import { watch, ref, computed } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import BasePaymentRequestsTable from './BasePaymentRequestsTable.vue'
import BatchFilterMultiSelect from './BatchFilterMultiSelect.vue'
import { useBatchSelection } from './composables/useBatchSelection'
import { buildBatchActionInputs, downloadFromUrls } from './composables/batchActions'
import { $t } from '@/composables/translation'
import { useToast } from '@/composables/useToast'
import { DataTableBulkAction } from '@/components/GraphqlDataTable/types'
import { RowSelectionMode } from '@/components/GraphqlDataTable/enums'
import { RunBatchActionMutation } from '@/components/GraphQL/mutations/Batch/RunBatchActionMutation'
import { BatchActionType, BatchItemStatus } from '@/gql/graphql'
import { graphql } from '@/gql'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'
import PaymentRequestActionColumnPayrolled from '@/components/GraphqlDataTable/columns/PaymentRequest/Partner/PaymentRequestActionColumnPayrolled.vue'

defineProps<{ isActiveTab?: boolean }>()

const query = graphql(`
  query PartnerPaymentRequestsPayrolledQuery(
    $accounts: [ID!]
    $search: String
    $first: Int!
    $page: Int
    $orderBy: [PaymentRequestOrderByClauseInput!]
    $timesheetPeriod: DateRangeInput
    $hasExpenses: Boolean
    $jobs: [ID!]
    $clients: [ID!]
    $batchIds: [ID!]
  ) {
    paymentRequests(
      accounts: $accounts
      search: $search
      first: $first
      page: $page
      orderBy: $orderBy
      timesheetPeriod: $timesheetPeriod
      hasExpenses: $hasExpenses
      jobs: $jobs
      clients: $clients
      batchIds: $batchIds
      statuses: [APPROVED]
      isPayrolled: true
    ) {
      data {
        ...PaymentRequestBatchColumn
        ...PaymentRequestPrIdColumn
        ...PaymentRequestWorkerColumn
        ...PaymentRequestClientColumn
        ...PaymentRequestTimesheetPeriodColumn
        ...PaymentRequestRateColumn
        ...PaymentRequestUnitsColumn
        ...PaymentRequestExpensesColumn
        ...PaymentRequestJobColumn
        ...PaymentRequestAssignmentCompanyColumn
        ...PaymentRequestWorkLocationColumn
        ...PaymentRequestBatchCreatedAtColumn
        ...PaymentRequestPayrolledAtColumn
        ...PaymentRequestActionColumnPayrolled
      }
    }
  }
`) as TypedQueryDocumentNode

const batchQuery = graphql(`
  query BatchFilterPayrolledQuery(
    $accounts: [ID!]
    $containsItemsWithStatus: [BatchItemStatus!]
    $first: Int!
  ) {
    batches(accounts: $accounts, containsItemsWithStatus: $containsItemsWithStatus, first: $first) {
      paginatorInfo {
        total
      }
      data {
        id
        name
        type
        processedItemsCount: itemsCountByStatus(status: [PROCESSED])
      }
    }
  }
`) as TypedQueryDocumentNode

const { result: batchResult } = useQuery(
  batchQuery,
  () => ({
    accounts: [currentAccount.globalId],
    containsItemsWithStatus: [BatchItemStatus.Processed],
    first: PER_PAGE_MAXIMUM,
  }),
  { clientId: 'publicApi' },
)

const batchSelection = useBatchSelection()
const selectionMode = ref<RowSelectionMode>(RowSelectionMode.PAGE)

watch(batchResult, (newResult) => {
  if (newResult?.batches) {
    batchSelection.updateBatches(newResult.batches.data)
  }
})

// Don't automatically switch to ALL mode when batch is selected - only for filtering
const enabledRowSelectionModes = computed<RowSelectionMode[]>(() => [RowSelectionMode.PAGE])

const handleBatchSelectionUpdated = async (ids: string[]) => {
  const current = new Set(Object.keys(batchSelection.selectedBatches.value))
  current.forEach((id) => {
    if (!ids.includes(id)) batchSelection.deselectBatch(id)
  })
  ids.forEach((id) => batchSelection.selectBatch(id))
  // Don't change selection mode when batch filter changes
}

const toast = useToast()

defineEmits<{
  'payment-request-updated': []
}>()

const { mutate: runBatchAction } = useMutation(RunBatchActionMutation, {
  clientId: 'publicApi',
})

const bulkActions = computed<DataTableBulkAction[]>(() => [
  {
    name: $t('Export'),
    icon: 'download' as any,
    type: 'secondary',
    action: async (selectedRows, scope, excludedIds, totalCount) => {
      const paymentRequestIds = Object.keys(selectedRows)

      try {
        const inputs = buildBatchActionInputs({
          selectedRows,
          excludedIds: excludedIds || [],
          isFullScope: scope === 'full-scope',
          selectedBatchIds: batchSelection.selectedBatchIds.value,
          action: BatchActionType.Export,
          deleteIfEmptied: false,
          status: scope === 'full-scope' ? BatchItemStatus.Processed : undefined,
        })
        const urls = await Promise.all(
          inputs.map((input) =>
            runBatchAction({ input }).then((res) => res?.data?.runBatchAction?.resultUrl),
          ),
        )

        await downloadFromUrls(urls)

        const count =
          scope === 'full-scope'
            ? Math.max(0, (totalCount || 0) - ((excludedIds as any)?.length || 0))
            : paymentRequestIds.length
        toast.success($t('Successfully exported :count payment requests', { count }))
      } catch (error) {
        toast.error($t('Failed to export payment requests. Please try again.'))
      }
    },
  },
])
</script>

<style scoped></style>
